/** Item Routes — item first.
 *
 * A route belongs to an item: "this item goes through these steps". The flat
 * version made you name the item on every row, which meant choosing it three
 * times to describe a three-step route, and it could not tell you which items
 * already had one.
 *
 * So: choose an item once, then work on its operations beneath. The item list
 * offers only items marked **Has operations** on the item master — FKMT's own
 * answer to "which items need a route?", because the system cannot know and a
 * person can.
 *
 * The stages shown are DERIVED, not stored. `erp_route_stages` walks the route
 * and numbers a repeated state — STRESS_RELIEVED then STRESS_RELIEVED_2 — so
 * what you see here is exactly what the stock ledger will use. A second copy
 * would eventually disagree with the first.
 *
 * JOB_WORK.md §4 and §7A.
 */

import { useCallback, useEffect, useMemo, useState } from 'react'
import { api, ApiError } from '@/lib/api'
import { SearchSelect } from '@/components/SearchSelect'
import { useObjectRights } from '@/lib/rights'

interface Operation {
  op_seq: number
  process_code: string
  process_name: string
  input_stage: string
  result_stage: string
  is_job_work: boolean
}
interface RouteResult {
  item: { item_id: string; item_name: string }
  operations: Operation[]
}
interface RoutableItem { item_id: string; item_name: string; operations: number }
interface Process { process_code: string; name: string; result_stage: string | null }

/** A stage code as people read it: STRESS_RELIEVED_2 → "Stress relieved - 2".
 *  The same convention the project management app uses for a later pass. */
function stageLabel(code: string): string {
  if (!code) return ''
  const m = /^(.*?)_(\d+)$/.exec(code)
  const base = (m ? m[1] : code).replace(/_/g, ' ').toLowerCase()
  const pretty = base.charAt(0).toUpperCase() + base.slice(1)
  return m ? `${pretty} - ${m[2]}` : pretty
}

export function ItemRouteScreen() {
  const [items, setItems] = useState<RoutableItem[]>([])
  const [processes, setProcesses] = useState<Process[]>([])
  const [itemId, setItemId] = useState('')
  const [route, setRoute] = useState<RouteResult | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)
  const [adding, setAdding] = useState(false)
  // masters.item_route.add / delete — what masters/index.py checks.
  const rights = useObjectRights('masters', 'item_route')
  const [draft, setDraft] = useState({ op_seq: '', process_code: '',
                                       is_job_work: 'true', description: '' })

  const loadItems = useCallback(async () => {
    try {
      const r = await api.get<{ rows: RoutableItem[] }>('/masters/item-routes/items')
      setItems(r.rows)
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Items could not be loaded.')
    }
  }, [])

  useEffect(() => { void loadItems() }, [loadItems])

  useEffect(() => {
    void (async () => {
      try {
        // Operations only — a returnable delivery changes nothing and does
        // not belong on a route.
        const r = await api.get<{ rows: Process[] }>('/masters/processes/',
                                                     { kind: 'Operation' })
        setProcesses(r.rows)
      } catch { /* the select simply offers nothing; the server still validates */ }
    })()
  }, [])

  const loadRoute = useCallback(async (id: string) => {
    if (!id) { setRoute(null); return }
    setBusy(true); setError(null)
    try {
      setRoute(await api.get<RouteResult>(`/masters/item-routes/${id}`))
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'The route could not be loaded.')
      setRoute(null)
    } finally { setBusy(false) }
  }, [])

  useEffect(() => { void loadRoute(itemId) }, [itemId, loadRoute])

  // 10, 20, 30 — spaced so an operation can be inserted later without
  // renumbering everything after it.
  const nextSeq = useMemo(() => {
    const last = route?.operations.at(-1)?.op_seq ?? 0
    return String(last + 10)
  }, [route])

  async function addOperation(e: React.FormEvent) {
    e.preventDefault()
    setBusy(true); setError(null)
    try {
      await api.create('/masters/item-routes/', {
        op_seq: Number(draft.op_seq || nextSeq),
        item_id: itemId,
        process_code: draft.process_code,
        is_job_work: draft.is_job_work === 'true',
        description: draft.description || null,
      })
      setAdding(false)
      setDraft({ op_seq: '', process_code: '', is_job_work: 'true', description: '' })
      await loadRoute(itemId)
      await loadItems()
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'The operation could not be added.')
    } finally { setBusy(false) }
  }

  // Only the last operation can go, and only while no stock has ever been at
  // the stage it produces. Both rules live on the server; this only asks.
  const lastSeq = route?.operations.at(-1)?.op_seq

  async function removeOperation(op: Operation) {
    const ok = confirm(
      'Remove operation ' + op.op_seq + ' — ' + op.process_name + '?')
    if (!ok) return
    setBusy(true); setError(null)
    try {
      await api.remove('/masters/item-routes/' + op.op_seq, { item_id: itemId })
      await loadRoute(itemId)
      await loadItems()
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'It could not be removed.')
    } finally { setBusy(false) }
  }

  return (
    <>
      <h1 className="page-title">Item Routes</h1>
      <p className="page-sub">
        Choose an item, then add the operations it goes through, in order.
      </p>

      {error && <div className="form-errors" role="alert">{error}</div>}

      <div className="card" style={{ marginBottom: 16 }}>
        <div className="toolbar">
          <label htmlFor="ri" style={{ fontSize: 13, fontWeight: 500 }}>Item</label>
          <div style={{ minWidth: 380 }}>
            <SearchSelect
              id="ri"
              value={itemId}
              emptyLabel="— choose an item —"
              onChange={v => { setItemId(v); setAdding(false) }}
              options={items.map(i => ({
                value: i.item_id,
                label: `${i.item_id} — ${i.item_name}`
                  + (i.operations ? ` (${i.operations} operations)`
                                  : ' (no route yet)'),
              }))}
            />
          </div>
          <div className="grow" />
          {itemId && !adding && rights.can('add') && (
            <button className="btn btn-primary" onClick={() => setAdding(true)}>
              Add Operation
            </button>
          )}
        </div>

        {items.length === 0 && (
          <div className="empty" style={{ padding: 24 }}>
            <p>No items are marked as needing a route.</p>
            <p>Set <strong>Has Operations</strong> to <em>Yes</em> on an item
               in <strong>Design → Items</strong>, and it will appear here.</p>
          </div>
        )}
      </div>

      {adding && (
        <form className="card" onSubmit={addOperation} style={{ marginBottom: 16 }}>
          <div className="card-head">Add Operation</div>
          <div className="form-grid">
            <div className="form-field">
              <label htmlFor="os">Operation No.</label>
              <input id="os" type="number" value={draft.op_seq}
                     placeholder={nextSeq}
                     onChange={e => setDraft({ ...draft, op_seq: e.target.value })} />
              <div className="hint">
                Leave blank for {nextSeq} — 10, 20, 30 leaves room to insert later
              </div>
            </div>
            <div className="form-field">
              <label htmlFor="pc">Process *</label>
              <SearchSelect
                id="pc"
                value={draft.process_code}
                onChange={v => setDraft({ ...draft, process_code: v })}
                options={processes.map(p => ({
                  value: p.process_code,
                  label: p.name + (p.result_stage
                    ? ` → ${stageLabel(p.result_stage)}` : ''),
                }))}
              />
              <div className="hint">The stage follows from the operation</div>
            </div>
            <div className="form-field">
              <label htmlFor="jw">Sent Outside</label>
              <select id="jw" value={draft.is_job_work}
                      onChange={e => setDraft({ ...draft, is_job_work: e.target.value })}>
                <option value="true">Yes — third-party job work</option>
                <option value="false">No — done in-house</option>
              </select>
              <div className="hint">
                An in-house operation still moves the stage; only an outside one
                raises a job work order
              </div>
            </div>
            <div className="form-field span-2">
              <label htmlFor="dsc">Description</label>
              <textarea id="dsc" value={draft.description}
                        onChange={e => setDraft({ ...draft, description: e.target.value })} />
            </div>
          </div>
          <div className="form-actions">
            <button className="btn btn-primary" disabled={busy}>
              {busy ? 'Saving…' : 'Add'}
            </button>
            <button type="button" className="btn btn-secondary"
                    onClick={() => setAdding(false)}>Cancel</button>
          </div>
        </form>
      )}

      {route && (
        <div className="card">
          <div className="card-head">
            {route.item.item_id} — {route.item.item_name}
          </div>
          {route.operations.length === 0 ? (
            <div className="empty" style={{ padding: 24 }}>
              <p>No operations yet.</p>
              <p>Add the first one and the stages follow from it.</p>
            </div>
          ) : (
            <div style={{ overflowX: 'auto' }}>
              <table>
                <thead>
                  <tr>
                    <th style={{ width: 80 }}>Op</th>
                    <th>Process</th>
                    <th>From</th>
                    <th>To</th>
                    <th style={{ width: 120 }}>Sent Outside</th>
                    <th style={{ width: 90 }} />
                  </tr>
                </thead>
                <tbody>
                  {route.operations.map(op => (
                    <tr key={op.op_seq}>
                      <td className="num">{op.op_seq}</td>
                      <td>{op.process_name}</td>
                      <td>{stageLabel(op.input_stage)}</td>
                      <td><strong>{stageLabel(op.result_stage)}</strong></td>
                      <td>{op.is_job_work ? 'Yes' : 'In-house'}</td>
                      <td>
                        {/* Offered only on the last operation: removing one
                            from the middle would change the stage every later
                            operation produces. */}
                        {op.op_seq === lastSeq && rights.can('delete') && (
                          <button className="link-btn"
                                  onClick={() => void removeOperation(op)}>
                            Remove
                          </button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}
    </>
  )
}
